import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { IMG } from "@/lib/images";
import { DevelopmentTracker } from "@/components/DevelopmentTracker";

const HIGHLIGHTS = [
  "Fidlock Buckle",
  "Orange Lining",
  "YKK Zippers",
  "Embroidered Patch",
  "Waterproof Membrane",
  "Hidden Pocket",
];

export const ProductTeaser = () => {
  return (
    <section id="outlaw-teaser" data-testid="teaser-section" className="relative py-28 md:py-40 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="section-mark" />
            <span className="overline">Currently in Prototype</span>
            <span className="section-mark" />
          </div>
          <h2 className="h-display text-white text-5xl sm:text-7xl lg:text-[8rem]" data-testid="teaser-headline">
            The <span className="text-[#F47A20]">OUTLAW V1</span>
            <br />
            is coming.
          </h2>
          <p className="mt-6 text-white/75 text-lg" data-testid="teaser-subheading">
            Prototype 001 is in development.
          </p>

          {/* Development status badge */}
          <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 border border-[#F47A20]/40 bg-[#F47A20]/8 rounded-sm" data-testid="dev-status-badge">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#F47A20] opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F47A20]" />
            </span>
            <span className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold uppercase">
              Prototype V1 &middot; Currently in Development
            </span>
          </div>
        </div>

        {/* Real interior reveal — the first piece of the OUTLAW V1 actually built */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-sm overflow-hidden border border-[#F47A20]/30 mb-10 bg-[#141417] glow-orange"
          data-testid="teaser-interior-reveal"
        >
          <img
            src={IMG.INTERIOR_LOGO}
            alt="OUTLAW V1 interior — embroidered RR logo on ROADRIG orange lining with woven label"
            loading="lazy"
            decoding="async"
            className="w-full h-auto block"
          />
          <div className="absolute top-4 left-4 tactical-badge text-[9px] bg-[#F47A20]/15">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F47A20] animate-pulse" />
            First Real Reveal · Interior Lining
          </div>
          <div className="absolute bottom-4 right-4 hidden md:block">
            <div className="serial text-[10px] text-white/80 text-right">
              EMBROIDERED RR · WOVEN LABEL · ROADRIG ORANGE
            </div>
          </div>
        </motion.div>

        {/* Confirmed components strip */}
        <div className="mb-12 p-5 md:p-6 bg-[#141417] border border-white/10 rounded-sm">
          <div className="serial text-[10px] text-[#F47A20] mb-4">CONFIRMED COMPONENTS · V1</div>
          <div className="flex flex-wrap gap-2">
            {HIGHLIGHTS.map((h, i) => (
              <span
                key={h}
                data-testid={`teaser-detail-${i}`}
                className="serial text-[10px] text-white/80 px-3 py-1.5 border border-white/10 rounded-sm bg-[#0A0A0C]"
              >
                {h}
              </span>
            ))}
          </div>
        </div>

        {/* Locked reveal banner */}
        <div className="relative p-6 md:p-8 bg-[#141417] border border-white/10 rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 grid place-items-center bg-[#1A1A1D] border border-[#F47A20]/40 rounded-sm">
              <Lock size={18} className="text-[#F47A20]" />
            </div>
            <div>
              <div className="serial text-[10px] text-[#F47A20] mb-1">FULL REVEAL · LOCKED</div>
              <div className="text-white text-lg font-semibold">The full OUTLAW V1 unlocks at launch.</div>
              <div className="text-white/60 text-sm mt-1">VIP members see it first — straight from the prototype shop.</div>
            </div>
          </div>
          <a href="#vip" data-testid="teaser-vip-cta" className="btn-primary">
            Unlock Early Access
          </a>
        </div>

        <DevelopmentTracker />
      </div>
    </section>
  );
};
